import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  format, startOfMonth, endOfMonth, isToday, addMonths, subMonths,
  startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, parseISO, addDays, subDays
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, X, Share2, Settings2 } from 'lucide-react';
import { api } from '../api';
import { cn } from '../lib/utils';

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const festDisplayStart = (f) => { const d = parseISO(f.date_start); return f.teach_on_departure_day ? addDays(d, 1) : d; };
const festDisplayEnd   = (f) => { const d = parseISO(f.date_end);   return f.teach_on_return_day   ? subDays(d, 1) : d; };

/* ── Conic gradient helper ─────────────────────────────────── */
function buildConicGradient(events) {
  if (!events.length) return null;
  const n = Math.min(events.length, 4);
  const segs = events.slice(0, n);
  if (n === 1) return segs[0].color;
  const stops = segs.map((ev, i) => {
    const from = (i / n * 100).toFixed(1);
    const to   = ((i + 1) / n * 100).toFixed(1);
    return `${ev.color} ${from}% ${to}%`;
  }).join(', ');
  return `conic-gradient(${stops})`;
}

/* ── Jour — mode partage : cercle / anneau coloré ─────────── */
function DayShare({ day, events, today }) {
  const dateStr = format(day, 'yyyy-MM-dd');
  const hasEvs  = events.length > 0;

  if (!hasEvs && !today) {
    return (
      <div className="flex flex-col items-center py-[1px]">
        <div className="w-[16px] h-[16px] flex items-center justify-center text-[7px] font-semibold text-slate-500">
          {day.getDate()}
        </div>
      </div>
    );
  }

  // Today sans événement : cercle bleu plein
  if (!hasEvs && today) {
    return (
      <div className="flex flex-col items-center py-[1px]">
        <div className="w-[16px] h-[16px] rounded-full bg-[#0288D1] flex items-center justify-center text-[7px] font-bold text-white">
          {day.getDate()}
        </div>
      </div>
    );
  }

  const grad = buildConicGradient(events);
  return (
    <div className="flex flex-col items-center py-[1px]">
      {/* Rond plein coloré avec le numéro en blanc au centre */}
      <div className="rounded-full flex items-center justify-center"
        style={{
          width: 18, height: 18,
          background: grad,
          fontSize: 7, fontWeight: 700,
          color: 'white',
          lineHeight: 1,
        }}>
        {day.getDate()}
      </div>
    </div>
  );
}

/* ── Mois mini — mode partage ─────────────────────────────── */
function MonthShare({ monthDate, eventsForDate }) {
  const firstDay = startOfMonth(monthDate);
  const lastDay  = endOfMonth(monthDate);
  const startPad = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  const d = new Date(firstDay);
  while (d <= lastDay) { cells.push(new Date(d)); d.setDate(d.getDate() + 1); }

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-2 select-none">
      <div className="text-[10px] font-bold text-slate-800 capitalize mb-1.5 font-manrope">
        {format(monthDate, 'MMMM', { locale: fr })}
        <span className="text-slate-400 font-normal ml-1 text-[9px]">{format(monthDate, 'yyyy')}</span>
      </div>
      <div className="grid grid-cols-7 mb-0.5">
        {WEEK_DAYS.map((l, i) => (
          <div key={i} className="text-center text-[7px] font-bold text-slate-300">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (!day) return <div key={`p${i}`} />;
          const evs   = eventsForDate(format(day, 'yyyy-MM-dd'));
          const today = isToday(day);
          return <DayShare key={format(day, 'yyyy-MM-dd')} day={day} events={evs} today={today} />;
        })}
      </div>
    </div>
  );
}

/* ── Mois complet — mode fonctionnel (même style que vue Mois) ── */
function MonthFull({ monthDate, classes = [], festivals = [] }) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd   = endOfMonth(monthDate);
  const calStart   = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd     = endOfWeek(monthEnd,     { weekStartsOn: 1 });
  const calDays    = eachDayOfInterval({ start: calStart, end: calEnd });
  const weekRows   = [];
  for (let i = 0; i < calDays.length; i += 7) weekRows.push(calDays.slice(i, i + 7));

  const classesByDay = {};
  classes.forEach(cls => {
    try {
      const key = format(parseISO(cls.datetime_start), 'yyyy-MM-dd');
      if (!classesByDay[key]) classesByDay[key] = [];
      classesByDay[key].push(cls);
    } catch {}
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60">
        <span className="font-extrabold text-slate-800 capitalize text-sm font-manrope">
          {format(monthDate, 'MMMM', { locale: fr })}
        </span>
        <span className="text-slate-400 text-xs font-normal ml-1.5">{format(monthDate, 'yyyy')}</span>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/30">
        {WEEK_DAYS.map(d => (
          <div key={d} className="text-center text-[10px] font-bold text-slate-400 py-1.5">{d}</div>
        ))}
      </div>
      {weekRows.map((weekRow) => {
        const weekStartStr = format(weekRow[0], 'yyyy-MM-dd');
        const weekEndStr   = format(weekRow[6], 'yyyy-MM-dd');
        const weekFests    = festivals.filter(f => {
          const ds = format(festDisplayStart(f), 'yyyy-MM-dd');
          const de = format(festDisplayEnd(f),   'yyyy-MM-dd');
          return ds <= weekEndStr && de >= weekStartStr;
        });
        return (
          <div key={weekStartStr}>
            {weekFests.length > 0 && (
              <div className="grid grid-cols-7 px-0.5 pt-0.5 gap-x-0">
                {weekFests.map(f => {
                  const dispStart = format(festDisplayStart(f), 'yyyy-MM-dd');
                  const dispEnd   = format(festDisplayEnd(f),   'yyyy-MM-dd');
                  const startIdx  = Math.max(0, weekRow.findIndex(d => format(d, 'yyyy-MM-dd') >= dispStart));
                  const endIdx    = Math.min(6, (() => {
                    for (let i = 6; i >= 0; i--) { if (format(weekRow[i], 'yyyy-MM-dd') <= dispEnd) return i; }
                    return 0;
                  })());
                  const clipL = dispStart < weekStartStr;
                  const clipR = dispEnd   > weekEndStr;
                  return (
                    <div key={f.id}
                      style={{ gridColumn: `${startIdx + 1} / ${endIdx + 2}`, backgroundColor: f.color || '#F59E0B' }}
                      className={cn(
                        'text-white text-[9px] font-bold px-1 py-0.5 truncate',
                        clipL ? 'rounded-r' : clipR ? 'rounded-l' : 'rounded',
                        f.optional ? 'opacity-40' : 'opacity-100'
                      )}>
                      {f.name}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-7 border-b border-slate-100">
              {weekRow.map(day => {
                const key      = format(day, 'yyyy-MM-dd');
                const dayCls   = classesByDay[key] || [];
                const inMonth  = isSameMonth(day, monthDate);
                const todayFlg = isToday(day);
                return (
                  <div key={key}
                    className={cn(
                      'min-h-[60px] p-1 border-r border-slate-100 last:border-r-0',
                      !inMonth && 'bg-slate-50/40'
                    )}>
                    <div className={cn(
                      'w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold mb-0.5',
                      todayFlg ? 'bg-[#0288D1] text-white' : (inMonth ? 'text-slate-700' : 'text-slate-300')
                    )}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-0.5">
                      {dayCls.slice(0, 2).map(cls => {
                        const color = cls.color || (cls.type === 'group' ? '#0288D1' : '#26A69A');
                        const time  = (() => { try { return format(parseISO(cls.datetime_start), 'HH:mm'); } catch { return ''; } })();
                        return (
                          <div key={cls.id} className="w-full rounded px-1 py-0.5 text-white leading-tight"
                            style={{ backgroundColor: color }}>
                            <div className="text-[9px] font-bold truncate">
                              {time} {cls.title || (cls.type === 'group' ? 'Collectif' : 'Privé')}
                            </div>
                          </div>
                        );
                      })}
                      {dayCls.length > 2 && (
                        <div className="text-[9px] font-bold text-slate-400 px-0.5">+{dayCls.length - 2}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Filter chip ───────────────────────────────────────────── */
function FilterChip({ label, color, active, onClick, dimmed = false }) {
  return (
    <button onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold border transition-all select-none',
        active ? 'text-white border-transparent shadow-sm' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
      )}
      style={active ? { backgroundColor: color, opacity: dimmed ? 0.55 : 1 } : {}}>
      <div className="w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: active ? 'rgba(255,255,255,0.8)' : color, opacity: dimmed ? 0.55 : 1 }} />
      <span className="truncate max-w-[110px]">{label}</span>
    </button>
  );
}

/* ── Main YearView ─────────────────────────────────────────── */
export default function YearView({ courses = [] }) {
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [displayMode, setMode]  = useState('functional'); // 'share' | 'functional'
  const [classes, setClasses]   = useState([]);
  const [festivals, setFests]   = useState([]);
  const [filters, setFilters]   = useState(() => {
    const s = new Set(['private', 'fest_ok', 'fest_opt']);
    courses.forEach(c => s.add(c.id));
    return s;
  });

  useEffect(() => {
    setFilters(prev => {
      const next = new Set(prev);
      courses.forEach(c => { if (!next.has(c.id)) next.add(c.id); });
      return next;
    });
  }, [courses]);

  const courseColorMap = useMemo(() => {
    const m = {};
    courses.forEach(c => { m[c.id] = c.color || '#0288D1'; });
    return m;
  }, [courses]);

  const rangeStart = format(startDate, 'yyyy-MM-dd');
  const rangeEnd   = format(new Date(startDate.getFullYear(), startDate.getMonth() + 12, 0), 'yyyy-MM-dd');

  const fetchData = useCallback(async () => {
    try {
      const [cls, fests] = await Promise.all([
        api.get(`/classes?start=${rangeStart}&end=${rangeEnd}`),
        api.get('/festivals'),
      ]);
      setClasses(cls || []);
      setFests(fests || []);
    } catch {}
  }, [rangeStart, rangeEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (key) => setFilters(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const eventsForDate = useCallback((dateStr) => {
    const evs = [];
    for (const cls of classes) {
      const d = (cls.datetime_start || '').slice(0, 10);
      if (d !== dateStr) continue;
      if (cls.type === 'group' && cls.group_course_id && filters.has(cls.group_course_id)) {
        evs.push({ color: courseColorMap[cls.group_course_id] || cls.color || '#0288D1' });
      } else if (cls.type === 'private' && filters.has('private')) {
        evs.push({ color: '#26A69A' });
      }
    }
    for (const f of festivals) {
      if (f.date_start > dateStr || f.date_end < dateStr) continue;
      if (f.optional && filters.has('fest_opt')) {
        evs.push({ color: f.color || '#F59E0B', opacity: 0.5 });
      } else if (!f.optional && filters.has('fest_ok')) {
        evs.push({ color: f.color || '#F59E0B' });
      }
    }
    return evs;
  }, [classes, festivals, filters, courseColorMap]);

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(startDate.getFullYear(), startDate.getMonth() + i, 1)
  );
  const endDate     = months[11];
  const periodLabel = `${format(startDate, 'MMM yyyy', { locale: fr })} – ${format(endDate, 'MMM yyyy', { locale: fr })}`;

  const isShare = displayMode === 'share';

  return (
    <div>
      {/* ── Barre de contrôle ── */}
      <div className="space-y-2 mb-4">

        {/* Sélecteur période + toggle mode */}
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center justify-between bg-white border border-slate-100 rounded-2xl shadow-sm px-3 py-2">
            <button onClick={() => setStartDate(d => subMonths(d, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="period-prev">
              <ChevronLeft size={18} className="text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              <select value={startDate.getMonth()}
                onChange={e => setStartDate(d => new Date(d.getFullYear(), +e.target.value, 1))}
                className="text-sm font-bold text-slate-700 bg-transparent border-0 focus:outline-none cursor-pointer"
                data-testid="start-month-select">
                {MONTHS_FR.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select value={startDate.getFullYear()}
                onChange={e => setStartDate(d => new Date(+e.target.value, d.getMonth(), 1))}
                className="text-sm font-bold text-slate-700 bg-transparent border-0 focus:outline-none cursor-pointer"
                data-testid="start-year-select">
                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 1 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button onClick={() => setStartDate(d => addMonths(d, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="period-next">
              <ChevronRight size={18} className="text-slate-600" />
            </button>
          </div>

          {/* Toggle vue */}
          <div className="flex bg-slate-100 rounded-xl p-1 gap-0.5 shrink-0">
            <button onClick={() => setMode('functional')}
              className={cn('flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all',
                !isShare ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
              data-testid="mode-functional-btn" title="Vue fonctionnelle">
              <Settings2 size={13} />
              <span className="hidden sm:inline">Perso</span>
            </button>
            <button onClick={() => setMode('share')}
              className={cn('flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all',
                isShare ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
              data-testid="mode-share-btn" title="Vue partage">
              <Share2 size={13} />
              <span className="hidden sm:inline">Partage</span>
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-3">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Afficher</div>
          <div className="flex flex-wrap gap-1.5">
            {courses.map(c => (
              <FilterChip key={c.id} label={c.name}
                color={c.color || '#0288D1'}
                active={filters.has(c.id)}
                onClick={() => toggle(c.id)} />
            ))}
            <FilterChip label="Cours privés" color="#26A69A"
              active={filters.has('private')} onClick={() => toggle('private')} />
            <FilterChip label="Festivals confirmés" color="#F59E0B"
              active={filters.has('fest_ok')} onClick={() => toggle('fest_ok')} />
            <FilterChip label="Festivals optionnels" color="#F59E0B"
              active={filters.has('fest_opt')} onClick={() => toggle('fest_opt')} dimmed />
          </div>
        </div>

        {/* Légende mode partage */}
        {isShare && (
          <div className="flex items-center gap-3 flex-wrap px-1">
            {courses.filter(c => filters.has(c.id)).map(c => (
              <span key={c.id} className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || '#0288D1' }} />
                {c.name}
              </span>
            ))}
            {filters.has('private') && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full bg-[#26A69A]" /> Privés
              </span>
            )}
            {(filters.has('fest_ok') || filters.has('fest_opt')) && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Festivals
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Grille mois ── */}
      {isShare ? (
        /* Mode partage : 3 × 4, cercles colorés */
        <div className="grid grid-cols-3 gap-2" data-testid="year-grid-share">
          {months.map((m, i) => (
            <MonthShare key={`${m.getFullYear()}-${m.getMonth()}`}
              monthDate={m} eventsForDate={eventsForDate} />
          ))}
        </div>
      ) : (
        /* Mode fonctionnel : 3 × 4, même présentation que la vue mois */
        <div className="grid grid-cols-3 gap-3" data-testid="year-grid-functional">
          {months.map((m, i) => (
            <MonthFull key={`${m.getFullYear()}-${m.getMonth()}`}
              monthDate={m} classes={classes} festivals={festivals} />
          ))}
        </div>
      )}
    </div>
  );
}
